import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { withAppHOC } from '../../hoc';
import {
  StyledContentContainer,
  StyledMainAreaContainer,
  StyledSpan,
} from '../../components/styled';
import PageHeader from '../../components/PageHeader/PageHeader';
import {
  Form,
  Fieldset,
  Label,
  TextInput,
  Dropdown,
  MessageBox,
  Button,
} from '../../components/FormElements';
import API from '../../api';
import { RESPONSE_STATUSES } from '../../constants';
import { getAuthData, getReturnUrl } from '../../utils';

const SellerMarginsUpdate = (props) => {
  const {
    userProfile: { isGSTSeller, gstWalletStatus, gstType },
  } = getAuthData();
  const { id } = useParams();
  const [state, setState] = useState({
    isLoading: false,
    responseStatus: '',
    responseMessage: '',
    apisId: '',
    operatorsId: '',
    circlesId: '',
    categoriesId: '',
    marginType:
      gstWalletStatus === 'Active' && isGSTSeller === 'Yes' ? 'GST' : 'Non-GST',
    operatorCode: '',
    amount: '',
    margin: '',
    maxLimit: '',
    marginStatus: 'Active',
    gstType: gstType,
    rofferMargin: '',
  });

  const readApis = async () => {
    try {
      const response = await API.get(`/seller/api-settings?fetchBalance=No`);
      return response?.data?.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const readMeta = async () => {
    try {
      const response = await API.get(`/meta`);
      return response?.data?.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const { data: apis } = useQuery({
    queryKey: ['apis'],
    queryFn: readApis,
  });

  const { data: meta } = useQuery({
    queryKey: ['meta'],
    queryFn: readMeta,
  });

  const apisOptions = [
    {
      name: '-- SELECT API --',
      value: '',
    },
  ];
  if (!isEmpty(apis) && apis.length > 0) {
    apis?.forEach(({ apiName, id }) =>
      apisOptions?.push({ name: apiName, value: id })
    );
  }

  const operatorsOptions = [
    {
      name: '-- SELECT OPERATOR --',
      value: '',
    },
  ];
  !isEmpty(meta?.operators) &&
    meta?.operators
      ?.filter(({ activeForSeller }) => activeForSeller === 'Active')
      ?.forEach(({ operatorName, id }) =>
        operatorsOptions?.push({ name: operatorName, value: id })
      );

  const circlesOptions = [
    {
      name: '-- SELECT CIRCLE --',
      value: '',
    },
  ];
  !isEmpty(meta?.circles) &&
    meta?.circles?.forEach(({ circleName, id }) =>
      circlesOptions?.push({ name: circleName, value: id })
    );

  const categoriesOptions = [
    {
      name: '-- SELECT CATEGORY --',
      value: '',
    },
  ];
  !isEmpty(meta?.categories) &&
    meta?.categories?.forEach(({ category, id }) =>
      categoriesOptions?.push({ name: category, value: id })
    );

  // const marginTypeOptions = [
  //   {
  //     name: '-- SELECT MARGIN TYPE --',
  //     value: '',
  //   },
  //   {
  //     name: 'GST',
  //     value: 'GST',
  //   },
  //   {
  //     name: 'Non-GST',
  //     value: 'Non-GST',
  //   },
  // ];

  const marginStatuOptions = [
    {
      name: '-- SELECT MARGIN STATUS --',
      value: '',
    },
    {
      name: 'Active',
      value: 'Active',
    },
    {
      name: 'Inactive',
      value: 'Inactive',
    },
  ];

  const readData = useCallback(async () => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      responseStatus: '',
      responseMessage: '',
    }));
    try {
      const response = await API.get(`/seller/margins/${id}`);
      const { status, message, data } = response.data;
      if (status === RESPONSE_STATUSES.SUCCESS) {
        setState((prevState) => ({
          ...prevState,
          apisId: data[0]?.apisId,
          operatorsId: data[0]?.operatorsId,
          circlesId: data[0]?.circlesId,
          categoriesId: data[0]?.categoriesId,
          marginType: data[0]?.marginType,
          operatorCode: data[0]?.operatorCode,
          amount: data[0]?.amount,
          margin: data[0]?.margin,
          maxLimit: data[0]?.maxLimit,
          marginStatus: data[0]?.marginStatus,
          gstType: data[0]?.gstType,
          rofferMargin: data[0]?.rofferMargin,
        }));
      } else {
        setState((prevState) => ({
          ...prevState,
          responseStatus: RESPONSE_STATUSES.FAILED,
          responseMessage: message,
        }));
      }
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        responseStatus: RESPONSE_STATUSES.FAILED,
        responseMessage: error.message,
      }));
    } finally {
      setState((prevState) => ({ ...prevState, isLoading: false }));
    }
  }, [id]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      responseStatus: '',
      responseMessage: '',
    }));
    try {
      const response = await API.put(`/seller/margins/${id}`, {
        operatorCode: state.operatorCode,
        margin: state.margin,
        maxLimit: state.maxLimit,
        marginStatus: state.marginStatus,
        amount: state.amount,
        categoriesId: state.categoriesId,
        rofferMargin: state.categoriesId === '2' || state.categoriesId === 2
          ? state.rofferMargin
          : 0,
      });
      const { status, message } = response.data;
      if (status === RESPONSE_STATUSES.SUCCESS) {
        setState((prevState) => ({
          ...prevState,
          responseStatus: RESPONSE_STATUSES.SUCCESS,
          responseMessage: message,
        }));
        setTimeout(() => {
          setState((prevState) => ({
            ...prevState,
            responseStatus: '',
            responseMessage: '',
          }));
          window.location.href = getReturnUrl('update');
        }, 3000);
      } else {
        setState((prevState) => ({
          ...prevState,
          responseStatus: RESPONSE_STATUSES.FAILED,
          responseMessage: message,
        }));
      }
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        responseStatus: RESPONSE_STATUSES.FAILED,
        responseMessage: error.message,
      }));
    } finally {
      setState((prevState) => ({ ...prevState, isLoading: false }));
    }
  };

  const renderPageHeading = () => (
    <PageHeader title="Update Margin" showBackButton={true} />
  );

  useEffect(() => {
    readData();
  }, [readData]);

  return (
    <>
      {renderPageHeading()}
      <StyledContentContainer>
        <StyledMainAreaContainer>
          <Form
            action="#"
            method="POST"
            onSubmit={onSubmit}
            style={{ maxWidth: '500px' }}
          >
            <Fieldset>
              <Label required>API</Label>
              <Dropdown
                value={state.apisId}
                onChange={(value) =>
                  setState((prevState) => ({
                    ...prevState,
                    apisId: value,
                  }))
                }
                placeholder="API"
                disabled={true}
                options={apisOptions}
              />
            </Fieldset>
            <Fieldset>
              <Label required>Operator</Label>
              <Dropdown
                value={state.operatorsId}
                onChange={(value) =>
                  setState((prevState) => ({
                    ...prevState,
                    operatorsId: value,
                  }))
                }
                placeholder="Operator"
                disabled={true}
                options={operatorsOptions}
              />
            </Fieldset>
            <Fieldset>
              <Label required>Circle</Label>
              <Dropdown
                value={state.circlesId}
                onChange={(value) =>
                  setState((prevState) => ({
                    ...prevState,
                    circlesId: value,
                  }))
                }
                placeholder="Circle"
                disabled={true}
                options={circlesOptions}
              />
            </Fieldset>
            <Fieldset>
              <Label required>Category</Label>
              <Dropdown
                value={state.categoriesId}
                onChange={(value) =>
                  setState((prevState) => ({
                    ...prevState,
                    categoriesId: value,
                  }))
                }
                placeholder="Category"
                disabled={true}
                options={categoriesOptions}
              />
            </Fieldset>
            {/* <Fieldset>
              <Label required>Margin Type</Label>
              <Dropdown
                value={state.marginType}
                onChange={(value) =>
                  setState((prevState) => ({
                    ...prevState,
                    marginType: value,
                  }))
                }
                placeholder="Margin Type"
                disabled={true}
                options={marginTypeOptions}
              />
            </Fieldset>
            {state.marginType === 'GST' && (
              <Fieldset>
                <Label required>GST Type</Label>
                <Dropdown
                  value={state.gstType}
                  onChange={(value) =>
                    setState((prevState) => ({
                      ...prevState,
                      gstType: value,
                    }))
                  }
                  placeholder="GST Type"
                  disabled={true}
                  options={[
                    { name: '-- SELECT GST TYPE --', value: '' },
                    {
                      name: 'P2P',
                      value: 'P2P',
                    },
                    {
                      name: 'P2A',
                      value: 'P2A',
                    },
                  ]}
                />
              </Fieldset>
            )} */}
            <Fieldset>
              <Label required>Operator Code</Label>
              <TextInput
                value={state.operatorCode}
                onChange={(value) =>
                  setState((prevState) => ({
                    ...prevState,
                    operatorCode: value,
                  }))
                }
                placeholder="Operator Code"
                disabled={state.isLoading}
              />
            </Fieldset>
            <Fieldset>
              <Label required>Amount</Label>
              <TextInput
                value={state.amount}
                isMarginAmount={true}
                onChange={(value) =>
                  setState((prevState) => ({
                    ...prevState,
                    amount: value,
                  }))
                }
                placeholder="Amount"
                disabled={state.isLoading}
              />
            </Fieldset>
            <Fieldset>
              <StyledSpan style={{ color: 'red', fontSize: '14px' }}>
                Note: Amount should be in one of the following formats. Ex:
                100,29 ( Multiple Denominations ) OR 10-200 ( Range ) OR 299 (
                Single Denomination ) OR 0 ( All Denominations )
              </StyledSpan>
            </Fieldset>
            <Fieldset>
              <Label required>Margin</Label>
              <TextInput
                value={state.margin}
                isNumeric={true}
                onChange={(value) =>
                  setState((prevState) => ({
                    ...prevState,
                    margin: value,
                  }))
                }
                placeholder="Margin"
                disabled={state.isLoading}
              />
            </Fieldset>
            {/* {(state.categoriesId === '2' || state.categoriesId === 2) && (
                <>
                  <Fieldset>
                    <Label required>Roffer Margin (%)</Label>
                    <TextInput
                      value={state.rofferMargin}
                      isNumeric={true}
                      onChange={(value) =>
                        setState((prevState) => ({
                          ...prevState,
                          rofferMargin: value,
                        }))
                      }
                      placeholder="Roffer Margin"
                      disabled={state.isLoading}
                    />
                  </Fieldset>
                  <Fieldset>
                    <StyledSpan style={{ color: 'red', fontSize: '14px' }}>
                      Note: Please enter ROFFER margin as displayed on ROFFER
                      Scree n in Lapu for example for Airtel 349 it display as
                      3.5
                    </StyledSpan>
                  </Fieldset>
                </>
              )} */}
            <Fieldset>
              <Label required>Max Limit</Label>
              <TextInput
                value={state.maxLimit}
                isNumeric={true}
                onChange={(value) =>
                  setState((prevState) => ({
                    ...prevState,
                    maxLimit: value,
                  }))
                }
                placeholder="Max Limit"
                disabled={state.isLoading}
              />
            </Fieldset>
            <Fieldset>
              <Label required>Margin Status</Label>
              <Dropdown
                value={state.marginStatus}
                onChange={(value) =>
                  setState((prevState) => ({
                    ...prevState,
                    marginStatus: value,
                  }))
                }
                placeholder="Margin Status"
                disabled={state.isLoading}
                options={marginStatuOptions}
              />
            </Fieldset>
            {state.responseStatus && state.responseMessage && (
              <Fieldset>
                <MessageBox
                  status={state.responseStatus}
                  message={state.responseMessage}
                />
              </Fieldset>
            )}
            <Fieldset>
              <Button disabled={state.isLoading}>Update</Button>
            </Fieldset>
          </Form>
        </StyledMainAreaContainer>
      </StyledContentContainer>
    </>
  );
};

export default withAppHOC(SellerMarginsUpdate);
